import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

/**
 * Format a match scheduled time in a user-friendly format
 * @param {string} scheduledTime - ISO date string
 * @returns {string} Formatted time string
 */
export const formatMatchTime = (scheduledTime) => {
  const date = new Date(scheduledTime);
  const options = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };
  return date.toLocaleString(undefined, options);
};

/**
 * Format a match scheduled time in the venue's timezone
 * @param {string} scheduledTime - ISO date string
 * @param {string} venueTimezone - IANA timezone string (e.g., 'Australia/Sydney')
 * @returns {string} Formatted time string with timezone abbreviation
 */
export const formatMatchTimeInVenueTimezone = (scheduledTime, venueTimezone) => {
  if (!venueTimezone) {
    return formatMatchTime(scheduledTime);
  }

  try {
    // Format: "Fri, Mar 15, 7:50 PM AEDT"
    return formatInTimeZone(
      new Date(scheduledTime),
      venueTimezone,
      "EEE, MMM d, h:mm a zzz"
    );
  } catch (error) {
    console.error('Error formatting time in venue timezone:', error);
    return formatMatchTime(scheduledTime);
  }
};

/**
 * Check if a match is locked (can't be picked)
 * @param {Object} match - Match object
 * @returns {boolean} True if match is locked
 */
export const isMatchLocked = (match) => {
  if (['live', 'awaiting_scores', 'completed', 'cancelled'].includes(match.status)) {
    return true;
  }
  const now = new Date();
  const kickoff = new Date(match.scheduledTime);
  return now >= kickoff;
};

/**
 * Get the status icon for a match
 * @param {Object} match - Match object
 * @returns {string|null} Icon emoji or null
 */
export const getMatchStatusIcon = (match) => {
  switch (match.status) {
    case 'live':
      return '🏈';
    case 'awaiting_scores':
      return '⏱️';
    case 'completed':
      return '✅';
    case 'cancelled':
      return '❌';
    default:
      return null;
  }
};

/**
 * Get pick result for a completed match
 * @param {Object} match - Match object with scores and pick
 * @returns {Object|null} Result object with type, points, and message
 */
export const getPickResult = (match) => {
  if (match.status !== 'completed' || !match.pick) {
    return null;
  }

  const { homeScore, awayScore, pick, homeTeam, awayTeam } = match;

  // Determine if draw
  if (homeScore === awayScore) {
    return {
      type: 'draw',
      points: pick.pointsEarned || 1,
      message: `Draw ${homeScore}-${awayScore}`,
    };
  }

  // Determine winner
  const winnerId = homeScore > awayScore ? homeTeam.id : awayTeam.id;
  const isCorrect = pick.pickedTeamId === winnerId;

  return {
    type: isCorrect ? 'correct' : 'wrong',
    points: pick.pointsEarned || (isCorrect ? 2 : 0),
    message: `${homeTeam.name} ${homeScore} - ${awayScore} ${awayTeam.name}`,
  };
};

/**
 * Calculate total points for a round
 * @param {Array} matches - Array of match objects
 * @returns {number} Total points earned
 */
export const calculateRoundTotal = (matches) => {
  return matches.reduce((total, match) => {
    if (match.status === 'completed' && match.pick && match.pick.pointsEarned !== null) {
      return total + match.pick.pointsEarned;
    }
    return total;
  }, 0);
};

/**
 * Format round date range
 * @param {Object} roundInfo - Round info object with startDate, endDate, seasonYear
 * @returns {string} Formatted date range string
 */
export const formatRoundDates = (roundInfo) => {
  if (!roundInfo || !roundInfo.startDate || !roundInfo.endDate) {
    return '';
  }

  // Parse dates using native Date constructor with explicit time to avoid timezone shifts
  const startDateStr = String(roundInfo.startDate).split('T')[0];
  const endDateStr = String(roundInfo.endDate).split('T')[0];

  const start = new Date(startDateStr + 'T12:00:00');
  const end = new Date(endDateStr + 'T12:00:00');

  const startStr = format(start, 'MMM d');
  const endStr = format(end, 'MMM d');

  return `${startStr} - ${endStr}, ${roundInfo.seasonYear}`;
};

/**
 * Check if a match is a BYE match
 * @param {Object} match - Match object
 * @returns {boolean} True if BYE match
 */
export const isByeMatch = (match) => {
  return match.isBye || !match.awayTeam;
};

/**
 * Sort matches: regular matches first (by scheduled time), BYEs last
 * @param {Array} matches - Array of match objects
 * @returns {Array} Sorted matches
 */
export const sortMatches = (matches) => {
  return [...matches].sort((a, b) => {
    const aIsBye = isByeMatch(a);
    const bIsBye = isByeMatch(b);

    // BYEs go to bottom
    if (aIsBye && !bIsBye) return 1;
    if (!aIsBye && bIsBye) return -1;

    // Otherwise maintain scheduled time order
    return new Date(a.scheduledTime) - new Date(b.scheduledTime);
  });
};
